import { Check, Clock, ArrowUpRight, BedDouble } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { wa } from "@/lib/site";

const ESSENCIAL_ITEMS = [
    "Diagnóstico estratégico",
    "Auditoria de Instagram",
    "Auditoria de WhatsApp",
    "Ajuste de bio",
    "Organização dos destaques",
    "Configuração do WhatsApp Business",
    "Catálogo",
    "Mensagem de boas-vindas",
    "8 posts personalizados",
    "2 Reels",
];

const RESERVAS_ITEMS = [
    "Briefing estratégico",
    "Auditoria de Instagram",
    "Auditoria de WhatsApp",
    "Auditoria do site",
    "Ajuste de bio",
    "Estrutura de comunicação",
    "Mensagem automatizada",
    "Organização dos principais pontos de contato",
    "Manual Comercial para Hotelaria",
];

const DELIVERY = "Entrega em até 7 dias úteis";

const ItemList = ({ items, testPrefix }) => (
    <ul className="flex flex-col gap-3.5">
        {items.map((item, i) => (
            <li
                key={item}
                data-testid={`${testPrefix}-item-${i + 1}`}
                className="flex items-start gap-3 text-sm sm:text-[15px] text-ink2 leading-relaxed"
            >
                <span className="mt-0.5 w-[18px] h-[18px] rounded-full border border-gold/40 bg-gold/10 flex items-center justify-center">
                    <Check size={10} className="text-gold" />
                </span>
                {item}
            </li>
        ))}
    </ul>
);

const DeliveryBadge = () => (
    <div className="flex items-center gap-2.5 rounded-full border border-line bg-bg2 px-4 py-2.5 w-fit">
        <Clock size={14} className="text-gold" />
        <span className="text-xs font-medium tracking-wide text-ink2">{DELIVERY}</span>
    </div>
);

const Products = () => (
    <section id="solucoes" data-testid="products-section" className="relative py-24 sm:py-32 scroll-mt-16">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <SectionHeading
                title="Soluções SETH"
                text="Estratégias práticas para negócios que querem transformar presença digital em oportunidade."
            />

            <div className="mt-14 grid lg:grid-cols-[1fr_1.12fr] gap-6 items-stretch">
                <Reveal className="h-full">
                    <article
                        data-testid="product-essencial-card"
                        className="card-hover h-full rounded-2xl border border-line bg-card p-8 sm:p-10 flex flex-col gap-8"
                    >
                        <div className="flex flex-col gap-4">
                            <span className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-ink3">
                                Para pequenos negócios
                            </span>
                            <h3 className="font-display text-2xl sm:text-3xl font-bold text-ink tracking-tight">
                                SETH ESSENCIAL<span className="text-gold align-super text-sm">™</span>
                            </h3>
                            <p className="text-[15px] text-ink2 leading-relaxed">
                                Para pequenos negócios que precisam colocar sua presença digital em ordem.
                            </p>
                        </div>

                        <ItemList items={ESSENCIAL_ITEMS} testPrefix="product-essencial" />

                        <div className="mt-auto flex flex-col gap-6 pt-2">
                            <DeliveryBadge />
                            <a
                                href={wa(
                                    "Olá! Tenho interesse no SETH ESSENCIAL™ e gostaria de saber mais."
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="product-essencial-cta"
                                className="btn-ghost inline-flex items-center justify-center gap-2 rounded-full border border-gold/50 px-7 py-4 text-sm font-semibold tracking-wide text-gold"
                            >
                                Quero conhecer o SETH ESSENCIAL™
                                <ArrowUpRight size={16} />
                            </a>
                        </div>
                    </article>
                </Reveal>

                <Reveal delay={0.12} className="h-full">
                    <div className="h-full rounded-2xl p-px bg-gradient-to-b from-[#BF953F] via-[#5a4a20] to-[#AA771C] shadow-[0_0_70px_rgba(212,175,55,0.10)]">
                        <article
                            data-testid="product-reservas-card"
                            className="h-full rounded-[calc(1rem-1px)] bg-[#101014] p-8 sm:p-10 flex flex-col gap-8 relative overflow-hidden"
                        >
                            <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_35%_at_50%_0%,rgba(212,175,55,0.10),transparent_70%)]" />
                            <div className="relative flex flex-col gap-4">
                                <div className="flex items-center justify-between">
                                    <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 font-mono2 text-[10px] uppercase tracking-[0.25em] text-gold">
                                        <BedDouble size={12} />
                                        Hotelaria
                                    </span>
                                    <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-ink3 hidden sm:block">
                                        Solução especializada
                                    </span>
                                </div>
                                <h3 className="font-display text-2xl sm:text-3xl font-bold text-ink tracking-tight">
                                    SETH <span className="text-gold-grad">+RESERVAS</span>
                                    <span className="text-gold align-super text-sm">™</span>
                                </h3>
                                <p className="text-[15px] text-ink2 leading-relaxed">
                                    Uma solução desenvolvida especialmente para hotéis, pousadas e chalés.
                                </p>
                            </div>

                            <div className="relative">
                                <ItemList items={RESERVAS_ITEMS} testPrefix="product-reservas" />
                            </div>

                            <div className="relative mt-auto flex flex-col gap-6 pt-2">
                                <DeliveryBadge />
                                <a
                                    href={wa(
                                        "Olá! Tenho um negócio de hotelaria e gostaria de conhecer o SETH +RESERVAS™."
                                    )}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="product-reservas-cta"
                                    className="btn-gold relative inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 text-sm font-semibold tracking-wide"
                                >
                                    Conhecer o +RESERVAS™
                                    <ArrowUpRight size={16} />
                                </a>
                            </div>
                        </article>
                    </div>
                </Reveal>
            </div>
        </div>
    </section>
);

export default Products;
